import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Save, 
  Upload, 
  Clock, 
  Eye, 
  X, 
  Edit3,
  Image as ImageIcon
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra
  };
};

const labelMap = {
  kicker: 'Hero Category Kicker',
  title: 'Hero Main Title',
  summary: 'Hero Summary Description'
};

export default function StudentLifeHeroForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Student Life',
    title: '',
    summary: '',
    slides: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    summary: false
  });

  const [pendingSlides, setPendingSlides] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [isAddingSlide, setIsAddingSlide] = useState(false);
  const [modalSlideData, setModalSlideData] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' })
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeHeroForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str || str === 'null' || str === 'undefined') return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) {
      const mapped = mediaMap[str];
      if (mapped.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${mapped}` : mapped;
      }
      return mapped;
    }

    if (mediaMap[mediaId]) {
      const mapped = mediaMap[mediaId];
      if (mapped.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${mapped}` : mapped;
      }
      return mapped;
    }

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let heroSection = null;

      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections`,
        { 
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          heroSection = result.data.find(s => s.section_type === 'student-life-hero');
        }
      }

      if (!heroSection) {
        const publicResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: getAuthHeaders()
          }
        );
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            heroSection = result.data.find(s => s.section_type === 'student-life-hero');
          }
        }
      }

      if (!heroSection) {
        throw new Error('Hero section not found');
      }

      setSectionId(heroSection.id);

      const content = heroSection.content_published || heroSection.content || {};
      const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
      const enData = parsedContent.en || parsedContent;

      let normalizedSlides = [];
      if (Array.isArray(enData.slides)) {
        normalizedSlides = enData.slides.slice(0, 3).map((s, idx) => {
          if (typeof s === 'string') return { image: s, order: idx + 1 };
          return { image: s.image || s.url || '', order: s.order || idx + 1 };
        });
      }

      const initialData = {
        kicker: enData.kicker || 'Student Life',
        title: enData.title || '',
        summary: enData.summary || '',
        slides: normalizedSlides
      };

      const pendingFieldsMap = { kicker: false, title: false, summary: false };
      const pendingSlidesMap = {};

      const pendingRes = await fetch(`${apiBase}/cms/section/${heroSection.id}/with-pending`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        if (pendingData.success && pendingData.data) {
          const pendingChanges = pendingData.data.pending_changes || [];
          
          if (Array.isArray(pendingChanges)) {
            pendingChanges.forEach((change) => {
              if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                let val = change.new_value;
                if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'summary') {
                  pendingFieldsMap[change.field_id] = true;
                  if (val !== undefined && val !== null) {
                    initialData[change.field_id] = val;
                  }
                } else if (change.field_id && change.field_id.includes('slides.')) {
                  const match = change.field_id.match(/slides\.(\d+)(?:\.(add|update|delete|image|order))?/);
                  if (match) {
                    const slideIdx = parseInt(match[1], 10);
                    const op = match[2] || 'update';
                    if (op === 'delete') {
                      pendingSlidesMap[`slide_${slideIdx}_delete`] = true;
                    } else if (op === 'add') {
                      pendingSlidesMap[`slide_${slideIdx}_add`] = true;
                      if (val && !initialData.slides[slideIdx]) {
                        initialData.slides.push(val);
                      }
                    } else {
                      pendingSlidesMap[`slide_${slideIdx}`] = true;
                      if (val && initialData.slides[slideIdx]) {
                        initialData.slides[slideIdx] = val;
                      }
                    }
                  }
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingFieldsMap);
      setPendingSlides(pendingSlidesMap);
    } catch (err) {
      console.error('[StudentLifeHeroForm] Load error:', err);
      toast.error('Error loading hero section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleOpenSlideModal = (index) => {
    setIsAddingSlide(false);
    setEditingSlideIndex(index);
    setModalSlideData({ ...formData.slides[index] });
  };

  const handleCloseSlideModal = () => {
    setEditingSlideIndex(null);
    setModalSlideData(null);
    setIsAddingSlide(false);
  };

  const handleSlideImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, WebP)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    const slideKey = `slide_${editingSlideIndex}`;
    setImageUploading(prev => ({ ...prev, [slideKey]: true }));
    setImageProgress(prev => ({ ...prev, [slideKey]: 0 }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('section_type', 'student-life-hero');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setImageProgress(prev => ({ ...prev, [slideKey]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const origin = (apiBase || '').replace(/\/api\/?$/, '');
              const returnedUrl = response.url || response.file_path || (response.filename ? `/api/uploads/studentlife/${response.filename}` : '');
              const fullUrl = returnedUrl.startsWith('http') ? returnedUrl : (returnedUrl ? `${origin}${returnedUrl}` : '');
              const finalUrl = fullUrl || returnedUrl || String(response.media_id || response.id || '');

              setModalSlideData(prev => ({
                ...prev,
                image: finalUrl
              }));

              const mediaId = response.media_id || response.id;
              if (mediaId) {
                setMediaMap(prev => ({
                  ...prev,
                  [mediaId]: finalUrl,
                  [String(mediaId)]: finalUrl
                }));
              }

              toast.success('Slide image uploaded successfully');
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (pe) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(prev => ({ ...prev, [slideKey]: false }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Network error during upload');
        setImageUploading(prev => ({ ...prev, [slideKey]: false }));
      });

      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(uploadFormData);
    } catch (err) {
      toast.error('Upload setup error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [slideKey]: false }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: labelMap[fieldName] || fieldName,
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${labelMap[fieldName] || fieldName} proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitSlideModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!modalSlideData || !modalSlideData.image) {
      toast.warning('Please upload an image for this slide');
      return;
    }

    const slideIndex = editingSlideIndex;
    const slideKey = `slide_${slideIndex}`;
    setSubmitting(prev => ({ ...prev, [slideKey]: true }));

    const fieldId = isAddingSlide ? `slides.${slideIndex}.add` : `slides.${slideIndex}.update`;
    const fieldName = isAddingSlide ? `Add Carousel Slide ${slideIndex + 1}` : `Update Carousel Slide ${slideIndex + 1}`;
    const oldValue = isAddingSlide ? null : (originalData.slides[slideIndex] || {});

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: modalSlideData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${fieldName} submitted for Principal approval`);

        if (isAddingSlide) {
          setFormData(prev => ({
            ...prev,
            slides: [...prev.slides, modalSlideData]
          }));
          setPendingSlides(prev => ({
            ...prev,
            [`slide_${slideIndex}_add`]: true
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            slides: prev.slides.map((s, i) => i === slideIndex ? modalSlideData : s)
          }));
          setPendingSlides(prev => ({
            ...prev,
            [slideKey]: true
          }));
        }

        setEditingSlideIndex(null);
        setModalSlideData(null);
        setIsAddingSlide(false);
      } else {
        if (response.status === 409) {
          toast.error(`Slide ${slideIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [slideKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Student Life hero section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Sparkles size={22} />
        </div>
        <div>
          <h2 className="unified-title">Student Life Hero Banner</h2>
          <p className="unified-subtitle">
            Manage the Student Life headline, summary description, and background carousel slides
          </p>
        </div>
      </div>

      {/* Field 1: Category Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Category Kicker Tag</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="50"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Student Life"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 2: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Hero Main Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="100"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Student Life at Permata Harapan"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 3: Summary */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Hero Summary Description</label>
          {pendingFields.summary && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="500"
            className="unified-textarea"
            rows="3"
            value={formData.summary}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            disabled={pendingFields.summary || submitting.summary}
            placeholder="Brief overview of student life, clubs, leadership, arts, and sports..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('summary')}
            disabled={submitting.summary || pendingFields.summary || formData.summary === originalData.summary}
          >
            <Save size={14} /> {submitting.summary ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 4: Carousel Slides */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Carousel Slides (3 Slides)</label>
            <p className="unified-field-desc">
              High-resolution background photos rotating in the Student Life hero section (Replace Image Only)
            </p>
          </div>
        </div>

        {formData.slides.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <ImageIcon size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No carousel slides configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Slide" above to upload banner photos</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginTop: '12px' }}>
            {formData.slides.map((slide, idx) => {
              const isPendingDelete = pendingSlides[`slide_${idx}_delete`];
              const isPendingAdd = pendingSlides[`slide_${idx}_add`];
              const isPendingUpdate = pendingSlides[`slide_${idx}`];
              const isPending = isPendingDelete || isPendingAdd || isPendingUpdate;
              const imgSrc = getMediaUrl(slide.image);

              return (
                <div
                  key={idx}
                  style={{
                    border: isPendingDelete 
                      ? '2px solid #ef4444' 
                      : isPendingAdd 
                        ? '2px solid #10b981' 
                        : isPendingUpdate 
                          ? '2px solid #f59e0b' 
                          : '1px solid #e2e8f0',
                    borderRadius: '14px',
                    background: isPendingDelete ? '#fef2f2' : isPending ? '#fffdfa' : '#ffffff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.8 : 1
                  }}
                >
                  {/* Thumbnail on top */}
                  <div
                    style={{
                      height: '150px',
                      background: '#0f172a',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {imgSrc ? (
                      <>
                        <img
                          src={imgSrc}
                          alt={`Slide ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => setLightboxImage(imgSrc)}
                        />
                        <button
                          type="button"
                          onClick={() => setLightboxImage(imgSrc)}
                          style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '11px',
                            gap: '4px'
                          }}
                          title="Zoom Image"
                        >
                          <Eye size={12} />
                        </button>
                      </>
                    ) : (
                      <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <ImageIcon size={28} />
                        <span style={{ fontSize: '12px' }}>Belum ada foto</span>
                      </div>
                    )}
                  </div>

                  {/* Card Body & Footer */}
                  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                        Slide #{idx + 1}
                      </span>
                      {isPendingDelete ? (
                        <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5', padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Deletion
                        </span>
                      ) : isPendingAdd ? (
                        <span className="unified-pending-pill" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0', padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Creation
                        </span>
                      ) : isPendingUpdate ? (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      ) : null}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        style={{ flex: 1, justifyContent: 'center' }}
                        onClick={() => handleOpenSlideModal(idx)}
                        disabled={submitting[`slide_${idx}`] || isPendingDelete}
                      >
                        <Edit3 size={13} /> {slide.image ? 'Change Photo' : 'Upload Photo'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Slide Edit Modal */}
      {editingSlideIndex !== null && modalSlideData && (
        <div className="unified-modal-overlay" onClick={handleCloseSlideModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                {isAddingSlide ? 'Add Carousel Slide' : `Edit Carousel Slide #${editingSlideIndex + 1}`}
              </h3>
              <button
                type="button"
                onClick={handleCloseSlideModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              <div style={{ marginBottom: '20px' }}>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Slide Image
                </label>
                
                {modalSlideData.image && (
                  <div
                    style={{
                      width: '100%',
                      height: '200px',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      marginBottom: '12px',
                      background: '#0f172a',
                      position: 'relative'
                    }}
                  >
                    <img
                      src={getMediaUrl(modalSlideData.image)}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                )}

                <label className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                  <Upload size={14} />
                  <span>
                    {imageUploading[`slide_${editingSlideIndex}`]
                      ? `Uploading ${imageProgress[`slide_${editingSlideIndex}`] || 0}%`
                      : modalSlideData.image ? 'Change Photo' : 'Upload Photo'}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleSlideImageUpload}
                    disabled={imageUploading[`slide_${editingSlideIndex}`]}
                    style={{ display: 'none' }}
                  />
                </label>
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '8px 0 0' }}>
                  Supported formats: JPEG, PNG, WebP (max 5MB)
                </p>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseSlideModal}
                disabled={submitting[`slide_${editingSlideIndex}`]}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitSlideModal}
                disabled={submitting[`slide_${editingSlideIndex}`] || !modalSlideData.image || imageUploading[`slide_${editingSlideIndex}`]}
              >
                <Save size={14} />
                {submitting[`slide_${editingSlideIndex}`] ? 'Submitting...' : 'Save & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                zIndex: 10
              }}
            >
              <X size={16} />
            </button>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', display: 'block' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
